import { phpCopy, strval, strtoupper, floatval, round, empty, intval, array_values, SORT_NUMERIC, sort, fromJson } from '../../php-compat';
import { wallRunSizeIdForLength } from '../../inventory/wall-run-size';
import { wallRunKeyNumber, wallRunPieceDescriptor } from '../../inventory/wall-run-piece';
import type { PortEnv, PhpRef } from '../../port-contract';
import { at, array, isArray, values, num, f, list, record, put, fail } from './support-b2';
/** Compatibility boundary for geometry callers; the native helper owns the ladder. */
export function ftWallRunSizeIdForLength(length: number): string {
    return wallRunSizeIdForLength(num(length));
}
/** PHP: ft_wall_run_carve_state (includes/functions/janus/items/wall_run_carve.php:54) */
export function ftWallRunCarveState(env: PortEnv): unknown {
    if (!isArray(env.globals.__ft_wall_run_carve_state))
        env.globals.__ft_wall_run_carve_state = record({ aoe: list([]), carved: list([]) });
    return env.globals.__ft_wall_run_carve_state;
}
/** PHP: ft_wall_run_length (includes/functions/janus/items/wall_run_carve.php:62) */
export function ftWallRunLength(run: unknown): unknown { return f(num(at(run, 'span', 'max')) - num(at(run, 'span', 'min'))); }
export function ftWallRunKeyNumber(value: number): string { return wallRunKeyNumber(num(value)); }
/** Adapt PHP-array callers to the shared birth/carve descriptor. */
export function ftWallRunPieceDescriptor(run: unknown, min: number, max: number): unknown {
    const sceneWallId = at(run, 'sceneWallId');
    return fromJson(wallRunPieceDescriptor({ axis: strval(at(run, 'axis')) as 'x' | 'y', at: num(at(run, 'at')),
        floorFt: num(at(run, 'floorFt') ?? at(run, 'z', 'min')), span: { min, max },
        z: { min: num(at(run, 'z', 'min')), max: num(at(run, 'z', 'max')) },
        ...(typeof sceneWallId === 'string' ? { sceneWallId } : {}) }));
}
/** PHP: ft_wall_run_along (includes/functions/janus/items/wall_run_carve.php:104) */
export function ftWallRunAlong(run: unknown, x: number, y: number): unknown { return floatval(strval(at(run, 'axis')) === 'x' ? y : x); }
/** PHP: ft_wall_run_piece_center (includes/functions/janus/items/wall_run_carve.php:110) */
export function ftWallRunPieceCenter(run: unknown, min: number, max: number): unknown { const mid = f((num(min) + num(max)) / 2), plane = floatval(at(run, 'at')); return strval(at(run, 'axis')) === 'x' ? record({ x: plane, y: mid }) : record({ x: mid, y: plane }); }
/** PHP: ft_wall_run_section_min (includes/functions/janus/items/wall_run_carve.php:124) */
export function ftWallRunSectionMin(run: unknown, along: number): unknown { const min = num(at(run, 'span', 'min')), max = num(at(run, 'span', 'max')), last = min + Math.floor((max - min - 1e-6) / 5) * 5, start = min + Math.floor((num(along) - min) / 5 + 1e-9) * 5; return round(Math.max(min, Math.min(last, start)), 4); }
/** PHP: ft_wall_run_section_max (includes/functions/janus/items/wall_run_carve.php:134) */
export function ftWallRunSectionMax(run: unknown, start: number): unknown { return round(Math.min(num(at(run, 'span', 'max')), num(start) + 5), 4); }
/** PHP: ft_wall_run_section_mins (includes/functions/janus/items/wall_run_carve.php:140) */
export function ftWallRunSectionMins(run: unknown): unknown {
    const out = [];
    for (let start = num(at(run, 'span', 'min')), max = num(at(run, 'span', 'max')); start < max - 1e-6; start += 5)
        out.push(round(start, 4));
    return list(out);
}
/** PHP: ft_wall_run_carve_pieces (includes/functions/janus/items/wall_run_carve.php:159) */
export function ftWallRunCarvePieces(run: unknown, mins: unknown): unknown {
    const grid = new Set(values(ftWallRunSectionMins(run)).map(strval)), starts = new Map<string, unknown>();
    for (const m of values(mins)) {
        const key = strval(round(floatval(m), 4));
        if (!grid.has(key))
            fail('InvalidArgumentException', 'section_start_off_grid');
        starts.set(key, floatval(key));
    }
    const out = [], sorted = values(sort(list([...starts.values()]), SORT_NUMERIC));
    let cursor = num(at(run, 'span', 'min'));
    for (const start of sorted) {
        if (num(start) > cursor + 1e-6)
            out.push(record({ min: f(cursor), max: start, section: false }));
        const end = ftWallRunSectionMax(run, num(start));
        out.push(record({ min: start, max: end, section: true }));
        cursor = num(end);
    }
    const max = floatval(at(run, 'span', 'max'));
    if (num(max) > cursor + 1e-6)
        out.push(record({ min: f(cursor), max, section: false }));
    return list(out);
}
export interface WallRunDeps {
    ft_inventory_generation(row: unknown): unknown;
    computed_value(row: unknown, cluster: string, key: string): unknown;
    set_pos_coord(row: PhpRef<unknown>, key: string, value: unknown): void;
    inventory_set(row: PhpRef<unknown>, key: string, value: unknown): void;
    item_field(row: unknown, key: string): unknown;
    get_inventory_arr(id: string): unknown;
    inventory_field(row: unknown, key: string): unknown;
}
/** PHP: ft_wall_run_aoe_sections (includes/functions/janus/items/wall_run_carve.php:198) */
export function ftWallRunAoeSections(inventory: unknown, covers: (row: unknown) => boolean, env: PortEnv<WallRunDeps>): unknown {
    const d = env.deps, g = d.ft_inventory_generation(inventory), run = isArray(at(g, 'wallRun')) ? at(g, 'wallRun') : null;
    if (run === null || num(ftWallRunLength(run)) <= 5 + 1e-6)
        return null;
    const vitality = d.computed_value(inventory, 'vitality', 'v_current') ?? d.computed_value(inventory, 'vitality', 'base_v_current');
    if (vitality !== null && num(vitality) <= 0)
        return null;
    const out = [];
    for (const start of values(ftWallRunSectionMins(run))) {
        const center = ftWallRunPieceCenter(run, num(start), num(ftWallRunSectionMax(run, num(start)))), section = { value: phpCopy(inventory) };
        d.set_pos_coord(section, 'x_pos', at(center, 'x'));
        d.set_pos_coord(section, 'y_pos', at(center, 'y'));
        d.inventory_set(section, 'inventory_size', ftWallRunSizeIdForLength(5));
        if (covers(section.value))
            out.push(start);
    }
    return list(out);
}
/** PHP: ft_wall_run_action_key (includes/functions/janus/items/wall_run_carve.php:228) */
export function ftWallRunActionKey(caster: unknown, item: unknown, env: PortEnv<WallRunDeps>): string { const c = strval(at(caster, 'encounter_creatures', 'encounter_creature_id') ?? env.deps.item_field(caster ?? list([]), 'item_id') ?? ''), a = strval(at(item, 'item_id_used', 0) ?? env.deps.item_field(item, 'item_id') ?? ''); return strtoupper(c) + '|' + strtoupper(a); }
/** PHP: ft_wall_run_aoe_plans_reset (includes/functions/janus/items/wall_run_carve.php:236) */
export function ftWallRunAoePlansReset(key: string, env: PortEnv): null { const state = array(ftWallRunCarveState(env)), aoe = array(at(state, 'aoe')); aoe.delete(key); state.set('aoe', aoe); env.globals.__ft_wall_run_carve_state = state; return null; }
/** PHP: ft_wall_run_aoe_plan_record (includes/functions/janus/items/wall_run_carve.php:243) */
export function ftWallRunAoePlanRecord(key: string, id: string, mins: unknown, env: PortEnv): null { const state = array(ftWallRunCarveState(env)); put(state, ['aoe', key, strtoupper(id)], array_values(mins as never)); env.globals.__ft_wall_run_carve_state = state; return null; }
/** PHP: ft_wall_run_aoe_planned (includes/functions/janus/items/wall_run_carve.php:250) */
export function ftWallRunAoePlanned(key: string, id: string, env: PortEnv): boolean { return at(ftWallRunCarveState(env), 'aoe', key, strtoupper(id)) !== null; }
/** PHP: ft_wall_run_live_rows (includes/functions/janus/items/wall_run_carve.php:315) */
export function ftWallRunLiveRows(ids: unknown, env: PortEnv<WallRunDeps>): unknown {
    const out = [], d = env.deps;
    for (const id of values(ids)) {
        const row = d.get_inventory_arr(strval(id));
        if (!empty(d.inventory_field(row, 'inventory_id')) && Number(intval(d.inventory_field(row, 'inventory_deleted') ?? 0)) !== 1)
            out.push(row);
    }
    return list(out);
}
